/* global Editor, classNames, PropTypes */

// Avoid conflict with x-editable plugin
const XEDITABLE_SELECTOR = 'editable-click'
const XEDITABLE_DATA_ATTR = 'x-editable-element'

export default class EditableText extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      active: false,
      text: props.text || '',
      hintText: props.hint || I18n.t('layouts.actions.click_to_edit')
    }

    this.editableContent = React.createRef()
    this.refInput = React.createRef()
    this.refEditor = React.createRef()
    this.shouldPersistOnDeactivate = false

    this.boundSave = this.save.bind(this)
    this.boundCancel = this.cancel.bind(this)
    document.addEventListener('saveEditableText', this.boundSave)
    document.addEventListener('cancelEditableText', this.boundCancel)

    this.isEditor = props.type === 'editor'
    this.isString = props.type === 'string'
    this.wrapperClass = props.wrapperClass || 'react-editable-text'
  }

  componentWillUnmount () {
    document.removeEventListener('saveEditableText', this.boundSave)
    document.removeEventListener('cancelEditableText', this.boundCancel)
  }

  componentDidUpdate () {
    if (this.isEditor) {
      if (this.state.active) {
        Editor.initTextEditor(this.refEditor.current, {
          simplifiedButtons: this.props.simplifiedButtons
        }).then((editor) => {
          if (!this.state.active) {
            editor.events && editor.destruct()
            return
          }

          this.editor = editor
        })
      } else if (this.editor) {
        this.editor.events && this.editor.destruct()
        this.editor = null
      }
    }
    if (!this.state.active && this.shouldPersistOnDeactivate) {
      if (_.isFunction(this.props.onSave)) {
        this.props.onSave(this.state.text)
      }
      this.shouldPersistOnDeactivate = false
    }
    if (this.props.relativeInput) $(this.props.relativeInput).val(this.state.text)
    if (this.editableContent.current) {
      $(this.editableContent.current).find(`.${XEDITABLE_SELECTOR}`).editable()
    }
  }

  render () {
    const { active } = this.state
    const wrapperClass = classNames({
      active,
      inactive: !active,
      'hints-hidden': this.props.hideHints
    }, this.wrapperClass)
    return (
      <div className={wrapperClass} onClick={this.activate.bind(this)}>
        {this.isEditor ? this.renderEditor() : this.renderString()}
        {this.renderButtonsOrHint()}
      </div>
    )
  }

  renderEditor () {
    const { active, text } = this.state
    if (active) {
      return <textarea ref={this.refEditor} defaultValue={text} style={{ display: 'none' }} />
    } else {
      return (
        <>
          <textarea
            ref={this.refEditor}
            defaultValue={text}
            disabled='disabled'
            style={{ display: 'none' }}
          />
          <div
            className={`editable-content ${!text && 'italic text-muted'}`} ref={this.editableContent}
            dangerouslySetInnerHTML={{ __html: text || this.props.placeholder }}
            onClick={this.activate.bind(this)}
          />
        </>
      )
    }
  }

  renderString () {
    const { active, text } = this.state
    if (active) {
      return <input type='text' ref={this.refInput} defaultValue={text} />
    } else {
      return (
        <div className={`editable-content ${!text && 'italic text-muted'}`} ref={this.editableContent}>
          {text || this.props.placeholder}
        </div>
      )
    }
  }

  renderButtonsOrHint () {
    const { active, hintText } = this.state
    if (active) {
      return (
        <>
          <button
            type='button'
            className='btn-success mt-2 me-1'
            onClick={this.save.bind(this)}
          >
            {I18n.t('layouts.actions.save')}
          </button>
          <button
            type='button'
            className='btn-default mt-2'
            onClick={this.cancel.bind(this)}
          >
            {I18n.t('layouts.common.cancel')}
          </button>
        </>
      )
    } else {
      if (this.props.hideHints) return null
      return (
        <div
          className='text-muted hint react-editable-text-hint hidden-print'
          data-hint-text={hintText}
        />
      )
    }
  }

  activate (e) {
    // prevent activation via checkbox/input/select/textarea click
    const inputNodes = ['INPUT', 'SELECT', 'TEXTAREA']
    if (inputNodes.indexOf(e.target.nodeName) !== -1) {
      e.target.addEventListener('change', this._updateInputNode.bind(this), { once: true })
      return
    }
    // set active
    if (!this.state.active) {
      if ($(e.target).is(`.${XEDITABLE_SELECTOR}`)) return // do not acivate editable area if click was on x-editable element
      const text = this._repropcessTextBeforeActivate(this.state.text)
      this.setState({ active: true, text })
    }
  }

  save () {
    if (this.state.active === false) return

    let text = this.isEditor ? (this.editor ? this.editor.value : this.refEditor.current.value) : this.refInput.current.value
    text = this._repropcessTextBeforeSave(text)
    this.shouldPersistOnDeactivate = true
    this.setState({ text, active: false })
  }

  cancel () {
    const text = this._repropcessTextBeforeSave(this.state.text)
    this.setState({ text, active: false })
  }

  _updateInputNode (e) {
    const node = e.target
    // Update attributes
    node.removeAttribute('checked') // clear for all
    if (node.nodeName === 'TEXTAREA') node.innerHTML = node.value
    else if (node.nodeName === 'SELECT') node.options[node.selectedIndex].setAttribute('selected', true)
    else if (node.type === 'radio' || node.type === 'checkbox') node.setAttribute('checked', node.checked)
    else node.setAttribute('value', node.value)

    // Update entire state
    if (this.editableContent.current) {
      const newText = this.editableContent.current.innerHTML
      this.shouldPersistOnDeactivate = true
      this.setState({ text: newText, active: false })
    }
  }

  _repropcessTextBeforeActivate (text) {
    if (text.search(XEDITABLE_SELECTOR) != -1 && this.editableContent.current) {
      // Find and store original x-editable element
      this.$xEditableElement = $(this.editableContent.current).find(`.${XEDITABLE_SELECTOR}`).detach()
      const xEditableElementText = this.$xEditableElement.text()
      const html = $(text)
      // replace x-editable element with its text
      html.find(`.${XEDITABLE_SELECTOR}`).replaceWith(`<span data-${XEDITABLE_DATA_ATTR}>${xEditableElementText}</span>`)
      return html.prop('outerHTML')
    } else {
      return text
    }
  }

  _repropcessTextBeforeSave (text) {
    if (this.$xEditableElement) {
      const html = $(text)
      // find x-editable text in editor
      const xEditableElementText = html.find(`[data-${XEDITABLE_DATA_ATTR}]`).text()
      // put x-editable text inside of x-editable element
      this.$xEditableElement.text(xEditableElementText)
      // Restore original x-editable element
      html.find(`[data-${XEDITABLE_DATA_ATTR}]`).replaceWith(this.$xEditableElement)
      this.$xEditableElement = null
      return html.prop('outerHTML')
    } else {
      return text
    }
  }
}

EditableText.propTypes = {
  text: PropTypes.string,
  type: PropTypes.oneOf(['editor', 'string']).isRequired,
  placeholder: PropTypes.string,
  onSave: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  hideHints: PropTypes.bool,
  relativeInput: PropTypes.string,
  wrapperClass: PropTypes.string,
  simplifiedButtons: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool
  ])
}
